import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { routes } from '@platform/core/lib/routes';
import type { IApiErrorPlain } from '@platform/core/types';
import { useMeQuery, useRecordLocationQuery, useWorkspaceQuery } from '@platform/state/api';
import { useAppDispatch } from '@platform/state/hooks';
import { workspaceSelected } from '@platform/state/slices/sessionSlice';
import { toastShown } from '@platform/state/slices/uiSlice';

/**
 * Посилання на запис з іншого робочого простору (ADR-028: простору немає в адресі): коли поточний простір запису не має (404 / 403),
 * сервер називає простір, модуль і тип, де користувач запис бачить (GET /me/records/{id}/location). Простір, який можна відкрити
 * (учасник або сервер дає його читати), стає поточним зі спливним «Відкрито в просторі «…»», адреса — канонічною для модуля й типу
 * (і в тому самому просторі, якщо адреса називає інший тип). Лише для простору, у якому запис відкрито: зміна простору на цій сторінці
 * (перемикач, сам перехід) нового пошуку не запускає. Повертає true, поки місце шукається чи запис переходить (сторінка показує
 * індикатор); запис, якого не видно ніде, — false.
 */
export function useRecordRelocation(ws: string, rtKey: string, id: string, tab: string, error: unknown): boolean {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const opened = useRef({ id, ws });
  if (opened.current.id !== id) opened.current = { id, ws };
  const status = (error as IApiErrorPlain | undefined)?.status;
  const missing = !!id && opened.current.ws === ws && (status === 404 || status === 403);
  const { data: location, isFetching } = useRecordLocationQuery(id, { skip: !missing });
  const elsewhere = !!location && location.workspaceId !== ws;
  const target = missing && location && (elsewhere || location.recordType !== rtKey) ? location : undefined;
  const { data: me } = useMeQuery();
  const member = me?.workspaces.find((w) => w.id === target?.workspaceId);
  const visited = useWorkspaceQuery(target?.workspaceId ?? '', { skip: !target || !elsewhere || !!member });
  const name = member?.name ?? visited.data?.name;
  useEffect(() => {
    if (!target || (elsewhere && name === undefined)) return;
    if (elsewhere) {
      dispatch(workspaceSelected(target.workspaceId));
      dispatch(toastShown({ kind: 'info', text: t('recordPage.openedIn', { name }) }));
    }
    const path = routes.record(target.module, target.recordType, id, tab || undefined);
    if (path !== pathname) navigate(`${path}${search}`, { replace: true });
  }, [target, elsewhere, name, id, tab, pathname, search, dispatch, navigate, t]);
  return missing && (isFetching || (!!target && !visited.isError));
}
